import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '@/components/ui/dialog'
import { SettingsPanel } from './settings-panel'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { fetchWithAuth } from '@/lib/api'

const settingsSchema = z.object({
  llm: z.object({
    defaultProvider: z.enum(['anthropic', 'openai', 'groq', 'xai', 'ollama']),
    defaultModel: z.string(),
    basecampIngestionProvider: z.enum(['anthropic', 'openai']),
    chatProvider: z.enum(['anthropic', 'openai', 'groq', 'xai', 'ollama']),
    enabledProviders: z.array(z.enum(['anthropic', 'openai', 'groq', 'xai', 'localai', 'ollama'])).optional()
  }),
  integrations: z.object({
    basecamp: z.object({
      enabled: z.boolean(),
      autoSync: z.boolean()
    }),
    googledrive: z.object({
      enabled: z.boolean()
    })
  }),
  ui: z.object({
    theme: z.enum(['light', 'dark', 'system']),
    defaultViewMode: z.enum(['card', 'grid', 'list']),
    sidebarCollapsed: z.boolean()
  }),
  features: z.object({
    voiceMemos: z.boolean(),
    aiSuggestions: z.boolean(),
    semanticSearch: z.boolean()
  })
})

type SettingsFormData = z.infer<typeof settingsSchema>

interface SettingsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** When set, open to this tab (e.g. 'organizations') */
  defaultTab?: string | null
  /** When set with tab `organizations`, drill into this workspace in Workspaces settings */
  focusOrganizationId?: string | null
  /** When set with tab `account`, scroll to a section (e.g. `password`) */
  focusSection?: string | null
}

export function SettingsDialog({ open, onOpenChange, defaultTab, focusOrganizationId, focusSection }: SettingsDialogProps) {
  const form = useForm<SettingsFormData>({
    resolver: zodResolver(settingsSchema),
    defaultValues: {
      llm: {
        defaultProvider: 'anthropic',
        defaultModel: 'claude-sonnet-5-5',
        basecampIngestionProvider: 'anthropic',
        chatProvider: 'anthropic',
        enabledProviders: ['anthropic']
      },
      integrations: {
        basecamp: {
          enabled: true,
          autoSync: true
        },
        googledrive: {
          enabled: false
        }
      },
      ui: {
        theme: 'system',
        defaultViewMode: 'card',
        sidebarCollapsed: false
      },
      features: {
        voiceMemos: true,
        aiSuggestions: true,
        semanticSearch: true
      }
    }
  })

  const handleSave = async (values: SettingsFormData): Promise<boolean> => {
    try {
      const response = await fetchWithAuth(`/api/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ settings: values })
      })
      const data = await response.json()
      if (data.success) {
        if (data.settings) {
          form.reset(data.settings)
        }
        return true
      }
      console.error('Failed to save settings:', data.error)
      return false
    } catch (error) {
      console.error('Failed to save settings:', error)
      return false
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="wide" className="flex flex-col overflow-hidden" showCloseButton={false}>
        <DialogHeader className="sr-only">
          <DialogTitle>Settings</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-hidden min-h-0">
          <SettingsPanel
            form={form}
            defaultTab={defaultTab}
            focusOrganizationId={focusOrganizationId}
            focusSection={focusSection}
            onSubmit={handleSave}
            onClose={() => onOpenChange(false)}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}
